/**
 * The journal's route.
 *
 * A directory rather than a flat file since PLAN-JOURNAL-LABELS D-JL7, because
 * a journal now has a Feedback page beside it, the blend's shape
 * (`routes/blending/$blendId/`). The page itself is unmoved and router-free.
 *
 * **The ticket.** "Journals" returns to the list entry this journal was opened
 * from; everything a step deeper — a batch, a blend, the label sheet — is
 * stamped with *this* entry (D-LS8). Feedback takes none: it comes straight
 * back here, and here is what holds the ticket.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { JournalPage } from '@/components/journal/journal-page';
import { useReturn, useReturnTicket } from '@/lib/return-ticket';

export const Route = createFileRoute('/journals/$journalId/')({
  component: JournalDetailPage,
});

function JournalDetailPage() {
  const { journalId } = Route.useParams();
  const navigate = useNavigate();
  // "Journals" goes back to the list this journal was opened from, keeping the
  // search and the scroll the brewer left there; a journal reached from a link
  // or a bookmark has no such entry and pushes `/journals` as it always did
  // (PLAN-LIST-STATE D-LS7).
  const toJournal = useReturn(() => {
    void navigate({ to: '/journals' });
  });
  // The attached batch is a step deeper, so the batch page it opens returns to
  // this entry — which is still holding the list's own ticket (D-LS8).
  const ticket = useReturnTicket();
  return (
    // Keyed on the id because the router reuses this component when only the
    // param changes, and a reused page keeps everything it was holding — an
    // open editor most of all. Each journal is a new mount. (The workspace
    // cache used to need this too; that half is fixed in
    // `store/workspace-context.tsx`.)
    <JournalPage
      key={journalId}
      journalId={journalId}
      onBack={toJournal}
      onFeedback={() => {
        void navigate({ to: '/journals/$journalId/feedback', params: { journalId } });
      }}
      // A fresh sheet with this journal on the chip row, carrying the ticket so
      // the label sheet's way out reaches the list this journal came from —
      // the blend page's bargain (D-JL7).
      onPrintLabels={() => {
        void navigate({ to: '/batches/labels', search: { beers: journalId }, state: ticket });
      }}
      onOpenBatch={(batchId) => {
        void navigate({ to: '/batches/$batchId', params: { batchId }, state: ticket });
      }}
      // The same step for the other kind of subject a block can have
      // (PLAN-UX-PASS F6): a blend links out to the journals it appears in, and
      // the block's name is the way back.
      onOpenBlend={(blendId) => {
        void navigate({ to: '/blending/$blendId', params: { blendId }, state: ticket });
      }}
    />
  );
}
